import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";

export const ReassignControl = ({ item, agents, onDone }) => {
  const [to, setTo] = useState("");
  const others = useMemo(() => agents.filter((a) => a.user_id !== item.user_id), [agents, item.user_id]);
  const go = () => api.post(`/sales/callbacks/${item.lead_id}/reassign`, { user_id: to })
    .then((r) => { toast.success(`Callback handed to ${r.data.agent}`); setTo(""); onDone(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="flex gap-2 pb-3 px-1" data-testid={`callback-reassign-${item.lead_id}`}>
      <select data-testid={`callback-reassign-select-${item.lead_id}`} value={to} onChange={(e) => setTo(e.target.value)} className="bg-muted/60 border h-8 px-2 text-xs flex-1 min-w-0 sm:flex-none sm:w-56">
        <option value="">Reassign to…</option>
        {others.map((a) => <option key={a.user_id} value={a.user_id}>{a.name}</option>)}
      </select>
      <button data-testid={`callback-reassign-submit-${item.lead_id}`} disabled={!to} onClick={go} className="border border-primary text-primary h-8 px-3 text-xs disabled:opacity-40">Hand over</button>
    </div>
  );
};

const pctTone = (v) => (v == null ? "text-muted-foreground" : v >= 80 ? "text-emerald-400" : v >= 50 ? "text-amber-400" : "text-red-400");

export const CallbackTrend = ({ refresh }) => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/sales/callbacks/trend").then((r) => setD(r.data)).catch(() => setD({ days: [], rows: [] })); }, [refresh]);
  if (!d) return null;
  const label = (x) => new Date(`${x}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "numeric" });
  return (
    <div className="ax-panel p-4 mb-4" data-testid="callback-trend">
      <div className="ax-label mb-1">On-time % · last 7 days</div>
      <p className="text-xs text-muted-foreground mb-3">— means no callbacks were due that day.</p>
      {!d.rows.length ? <p className="text-sm text-muted-foreground" data-testid="callback-trend-empty">No callbacks in the last 7 days.</p> : (
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1 pr-2">Agent</th>{d.days.map((x) => <th key={x} className="pr-2 whitespace-nowrap">{label(x)}</th>)}<th>Week</th></tr></thead>
          <tbody>{d.rows.map((r) => (
            <tr key={r.user_id} className="border-t border-border/50" data-testid={`callback-trend-row-${r.user_id}`}>
              <td className="py-2 pr-2 font-medium whitespace-nowrap">{r.agent}</td>
              {d.days.map((x) => { const v = r.days[x]; return <td key={x} className={`num pr-2 ${pctTone(v)}`}>{v == null ? "—" : `${v}%`}</td>; })}
              <td className={`num font-semibold ${pctTone(r.week_pct)}`} data-testid={`callback-trend-week-${r.user_id}`}>{r.week_pct == null ? "—" : `${r.week_pct}%`}</td>
            </tr>))}</tbody>
        </table></div>)}
    </div>
  );
};

export const CallbackScore = ({ refresh }) => {
  const today = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
  const [date, setDate] = useState(today);
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/sales/callbacks/score", { params: { date } }).then((r) => setRows(r.data.rows)).catch(() => setRows([])); }, [date, refresh]);
  return (
    <div className="ax-panel p-4 mb-4" data-testid="callback-score">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <span className="ax-label">Daily callback score</span>
        <input type="date" data-testid="callback-score-date" value={date} max={today} onChange={(e) => setDate(e.target.value || today)} className="bg-muted/60 border h-8 px-2 text-xs" />
        <span className="text-xs text-muted-foreground">On time = actioned within 30 min of the callback time. Reassigned callbacks count as missed.</span>
      </div>
      {!rows ? null : !rows.length ? <p className="text-sm text-muted-foreground" data-testid="callback-score-empty">No callbacks were due on this day.</p> : (
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1 pr-2">Agent</th><th className="pr-2">On time</th><th className="pr-2">Late</th><th className="pr-2">Missed</th><th className="pr-2">Open</th><th>%</th></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.user_id} className="border-t border-border/50" data-testid={`callback-score-row-${r.user_id}`}>
              <td className="py-2 pr-2 font-medium">{r.agent}</td><td className="num pr-2 text-emerald-400">{r.on_time}</td><td className="num pr-2 text-amber-400">{r.late}</td>
              <td className="num pr-2 text-red-400">{r.missed}</td><td className="num pr-2">{r.open}</td>
              <td className={`num font-semibold ${pctTone(r.on_time_pct)}`}>{r.on_time_pct == null ? "—" : `${r.on_time_pct}%`}</td>
            </tr>))}</tbody>
        </table></div>)}
    </div>
  );
};
